import React from 'react';
import { Icon } from '@iconify/react';
import { InteractionState } from '../../types';
import { calculateModalPosition, truncateText } from '../../utils';
import { PLACEHOLDERS, UI_CONFIG } from '../../constants';

interface CommentModalProps {
  interactionState: InteractionState;
  instructionText: string;
  isEditMode: boolean;
  onInstructionChange: (text: string) => void;
  onSubmit: () => void;
  onClose: () => void;
}

/**
 * CommentModal - Floating modal for adding element comments or modifications
 */
export const CommentModal: React.FC<CommentModalProps> = ({
  interactionState,
  instructionText,
  isEditMode,
  onInstructionChange,
  onSubmit,
  onClose,
}) => {
  if (!interactionState.isActive || !interactionState.position) {
    return null;
  }

  const position = calculateModalPosition(
    interactionState.position.x,
    interactionState.position.y,
    UI_CONFIG.MODAL.WIDTH,
    UI_CONFIG.MODAL.MAX_HEIGHT
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSubmit();
    }
    if (e.key === 'Escape') {
      onClose();
    }
  };

  return (
    <div
      className="fixed z-50 bg-content1 shadow-xl rounded-lg p-3 border border-default-200 w-80 animate-in fade-in zoom-in-95 duration-200"
      style={{
        left: position.left,
        top: position.top,
      }}
    >
      <div className="flex justify-between items-center mb-2">
        <span className="text-xs font-semibold text-default-500 uppercase">
          {isEditMode ? 'Add Comment' : 'Modify'}: {truncateText(interactionState.selectedText || '', 20)}
        </span>
        <button
          onClick={onClose}
          className="text-default-400 hover:text-default-500 transition-colors"
        >
          <Icon icon="material-symbols:close" width={16} />
        </button>
      </div>

      <textarea
        autoFocus
        className="w-full text-sm border rounded-md p-2 mb-2 focus:ring-2 focus:ring-blue-500 outline-none resize-none"
        placeholder={isEditMode ? PLACEHOLDERS.COMMENT_INPUT : PLACEHOLDERS.MODIFY_INPUT}
        rows={3}
        value={instructionText}
        onChange={(e) => onInstructionChange(e.target.value)}
        onKeyDown={handleKeyDown}
      />

      <button
        onClick={onSubmit}
        className="w-full bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium py-1.5 rounded-md transition-colors flex items-center justify-center gap-1"
      >
        {isEditMode ? (
          <>
            <Icon icon="material-symbols:add-comment" width={14} />
            Add Comment
          </>
        ) : (
          <>
            Apply Change <Icon icon="material-symbols:arrow-forward" width={14} />
          </>
        )}
      </button>
    </div>
  );
};
